import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  AUTO_AUTHORIZE_DEFAULT_CAP,
  AUTO_AUTHORIZE_DURATIONS,
  AUTO_AUTHORIZE_DURATION_LABELS,
  AUTO_AUTHORIZE_MAX_CAP,
  MIGRATION_VERDICT_LABELS,
  MOVE_CLASS_LABELS,
  ONU_PRESENCE_LABELS,
  parsePonSlot,
  type AuthorizePreset,
  type AutoAuthorizeDuration,
  type AutoAuthorizeWindow,
  type MigrationPlan,
  type MigrationPlanItem,
  type MigrationVerdict,
  type MigrationVlanResolution,
  type Olt,
  type Page,
  type PonPort,
  type UplinkPort,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import {
  Button,
  Card,
  ErrorNotice,
  Field,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
  type PillTone,
} from '../../components/ui';

/**
 * ONU migration for one chassis: the plan first, the armed window second.
 *
 * That order is the feature. Auto-authorising is the most dangerous thing this
 * platform does, so what an operator sees when they open this is a dry run —
 * exactly which ONUs would move, which of the three kinds of move each one is,
 * and what configuration each would be given — and the toggle underneath it is
 * only permission to run that. Nothing here is armed by default and the arm
 * always names a duration.
 */

const VERDICT_TONE: Record<MigrationVerdict, PillTone> = {
  migration: 'success',
  duplicate: 'danger',
  unverified: 'warn',
  preset_incomplete: 'warn',
  new_subscriber: 'neutral',
  same_slot: 'neutral',
  capped: 'info',
  out_of_scope: 'neutral',
};

/** mm:ss for anything under an hour, h m above it. */
function countdown(ms: number): string {
  if (ms <= 0) return 'expired';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function OnuMigrationDialog({
  olt,
  onClose,
  invalidate,
}: {
  olt: Olt;
  onClose: () => void;
  /** Extra query keys to refresh once the migration lands — the mounting page's
   * own lists (e.g. the Devices "awaiting" card) on top of the shared olts/onus. */
  invalidate?: QueryKey[];
}) {
  const queryClient = useQueryClient();
  const tasks = useTasks();
  const base = `/olts/${encodeURIComponent(olt.id)}`;

  const [error, setError] = useState<string | null>(null);
  const [presetId, setPresetId] = useState('');
  const [duration, setDuration] = useState<AutoAuthorizeDuration>(AUTO_AUTHORIZE_DURATIONS[0]);
  const [cap, setCap] = useState(AUTO_AUTHORIZE_DEFAULT_CAP);
  const [sourceOltId, setSourceOltId] = useState('');
  // Source-side shelf/slot filter (`1/3`, …). Empty = every slot of the source.
  const [sourceSlots, setSourceSlots] = useState<string[]>([]);
  const [ponPort, setPonPort] = useState('');
  const [finalize, setFinalize] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [plan, setPlan] = useState<MigrationPlan | null>(null);
  // A carried VLAN the destination lacks needs a resolution before the move:
  // add it to an uplink here, or replace it on the ONU with a local one.
  const [vlanFixes, setVlanFixes] = useState<Map<number, MigrationVlanResolution>>(new Map());

  const presets = useQuery({
    queryKey: ['onu-presets'],
    queryFn: () => api.get<Page<AuthorizePreset>>('/onu-presets'),
  });
  const oltsQuery = useQuery({
    queryKey: ['olts', 'for-migration'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
  });
  const windowQuery = useQuery({
    queryKey: ['olt', olt.id, 'auto-authorize'],
    queryFn: () => api.get<{ window: AutoAuthorizeWindow | null }>(`${base}/auto-authorize`),
    // A window is a clock, so it is re-read while this is open rather than
    // trusted to stay true for as long as somebody leaves the dialog up.
    refetchInterval: 15_000,
  });
  // The destination's uplink ports — the "add to uplink" picker's options. Only
  // needed when a VLAN turns out to be missing, but the read is cheap and
  // cached, so it is fetched up front rather than on first mismatch.
  const uplinksQuery = useQuery({
    queryKey: ['olt', olt.id, 'uplinks'],
    queryFn: () => api.get<{ items: UplinkPort[] }>(`${base}/uplinks`),
  });
  // The chosen SOURCE OLT's PON ports, which the slot picker groups into
  // shelf/slot cards. Same key the OLT page's VLANs tab uses, so it is shared.
  const sourcePortsQuery = useQuery({
    queryKey: ['olt', sourceOltId, 'pon-ports'],
    queryFn: () => api.get<Page<PonPort>>(`/olts/${encodeURIComponent(sourceOltId)}/pon-ports`),
    enabled: Boolean(sourceOltId),
  });
  /** `shelf/slot` → how many PON ports that card has, in numeric order. */
  const sourceSlotOptions = useMemo(() => {
    const cards = new Map<string, number>();
    for (const p of sourcePortsQuery.data?.items ?? []) {
      const parsed = parsePonSlot(p.port);
      if (!parsed) continue;
      const key = `${parsed.shelf}/${parsed.slot}`;
      cards.set(key, (cards.get(key) ?? 0) + 1);
    }
    return [...cards.entries()]
      .map(([key, ports]) => ({ key, ports }))
      .sort((a, b) => {
        const [as = 0, ac = 0] = a.key.split('/').map(Number);
        const [bs = 0, bc = 0] = b.key.split('/').map(Number);
        return as - bs || ac - bc;
      });
  }, [sourcePortsQuery.data]);
  const armed = windowQuery.data?.window?.active ? windowQuery.data.window : null;

  /** Ticks the countdown without re-asking the server every second. */
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const remaining = armed ? armed.expiresAt - tick : 0;

  // No preset is auto-selected. A migration moves a unit that is already a
  // subscriber, so the default — an empty preset — carries the configuration
  // SimpleACS already holds for each ONU. A preset is the deliberate OVERRIDE,
  // for reconfiguring during the move; auto-picking one would silently rewrite
  // every moved subscriber's VLANs. (An armed window still requires one, and
  // the effect below fills it in when re-opening an armed window.)

  useEffect(() => {
    if (armed) {
      setPresetId(armed.presetId);
      setCap(armed.maxOnus);
      setSourceOltId(armed.sourceOltId ?? '');
      setSourceSlots(armed.sourceSlots ?? []);
      setPonPort(armed.ponPort ?? '');
      setFinalize(armed.finalize);
    }
  }, [armed?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = useMutation({
    mutationFn: () =>
      api.get<MigrationPlan>(`${base}/migrations/plan`, {
        presetId: presetId || undefined,
      }),
    onSuccess: (result) => {
      setPlan(result);
      setError(null);
      // Everything the plan says it would move is ticked; the operator unticks
      // rather than hunting for checkboxes.
      setChosen(
        new Set(result.items.filter((i) => i.verdict === 'migration').map((i) => i.serial)),
      );
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not build the plan'),
  });

  /**
   * The migration itself — a multi-ONU authorise onto this chassis, the
   * heaviest write the dialog starts. It CLOSES and runs in the background tray
   * like Resync, so an operator is not pinned to a spinner while a batch is
   * authorised; the toast reports how many moved, and a failure stays until
   * acknowledged. The plan/arm/preview above stay in the dialog — this is only
   * the final "Migrate N selected". The choice is captured into locals because
   * the dialog is gone by the time the toast settles.
   */
  const startRun = () => {
    // Only the VLANs the CHOSEN ONUs actually carry-and-miss are sent, each
    // resolved once — the server refuses any it is not told how to handle.
    const needed = [
      ...new Set(
        (plan?.items ?? [])
          .filter((i) => i.verdict === 'migration' && chosen.has(i.serial))
          .flatMap((i) => i.missingVlans),
      ),
    ];
    const vlanResolutions = needed
      .map((v) => vlanFixes.get(v))
      .filter((r): r is MigrationVlanResolution => Boolean(r));
    const serials = [...chosen];
    const count = chosen.size;
    onClose();
    tasks.start<{ migrated: unknown[]; blocked: unknown[]; errors: string[] }>({
      label: `Migrate ${count} ONU${count === 1 ? '' : 's'} onto ${olt.name}`,
      href: `/olts/${encodeURIComponent(olt.id)}`,
      run: () =>
        api.post<{ migrated: unknown[]; blocked: unknown[]; errors: string[] }>(
          `${base}/migrations/run`,
          {
            serials,
            presetId: presetId || undefined,
            ...(vlanResolutions.length > 0 ? { vlanResolutions } : {}),
            finalize,
            dryRun: false,
          },
        ),
      done: (result) =>
        `${result.migrated.length} migrated onto ${olt.name}` +
        // §8.6.5: report the BLOCKED ones too — a duplicate still live on the
        // old slot is exactly what an operator needs to see, and dropping it
        // is "tick ten, hear about eight".
        (result.blocked.length > 0 ? `, ${result.blocked.length} blocked` : '') +
        (result.errors.length > 0 ? `, ${result.errors.length} refused` : '') +
        '.',
      invalidate: [['olts'], ['onus'], ...(invalidate ?? [])],
    });
  };

  const arm = useMutation({
    mutationFn: () =>
      api.post<{ window: AutoAuthorizeWindow }>(`${base}/auto-authorize`, {
        durationMinutes: duration,
        presetId,
        sourceOltId: sourceOltId || null,
        // Slots only mean something on one chassis; none chosen = every slot.
        sourceSlots: sourceOltId && sourceSlots.length > 0 ? sourceSlots : null,
        ponPort: ponPort || null,
        maxOnus: cap,
        finalize,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['olt', olt.id, 'auto-authorize'] });
      await queryClient.invalidateQueries({ queryKey: ['auto-authorize'] });
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not arm the window'),
  });

  const disarm = useMutation({
    mutationFn: () => api.delete<{ wasArmed: boolean }>(`${base}/auto-authorize`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['olt', olt.id, 'auto-authorize'] });
      await queryClient.invalidateQueries({ queryKey: ['auto-authorize'] });
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not disarm the window'),
  });

  const otherOlts = (oltsQuery.data?.items ?? []).filter((o) => o.id !== olt.id);

  /*
    Until 2026-09-19 a ZTE C650 destination was gated here on the OLT DTO's
    `refusals.onu_ip_hosts`: a preset with a management VLAN, TR-069 or a
    routed subscriber disabled Arm and Migrate, and every planned row that
    would need the ONU's own IP interfaces was marked. The migration composes
    through the same builder as the authorise wizard, which plans no ip-hosts
    on a C6xx now (the management write's `wan-ip … host 2` path and the CPE's
    TR-069 WAN do that job), so the gate and its marks are gone.
  */

  const movable = useMemo(
    () => (plan?.items ?? []).filter((i) => i.verdict === 'migration'),
    [plan],
  );

  // VLANs the CHOSEN ONUs would carry that the destination lacks — one decision
  // each, since a VLAN missing across many ONUs is resolved once.
  const missingVlans = useMemo(() => {
    const picked = (plan?.items ?? []).filter(
      (i) => i.verdict === 'migration' && chosen.has(i.serial),
    );
    return [...new Set(picked.flatMap((i) => i.missingVlans))].sort((a, b) => a - b);
  }, [plan, chosen]);

  const vlanFixComplete = (v: number) => {
    const fix = vlanFixes.get(v);
    if (!fix) return false;
    return fix.action === 'add-to-uplink' ? Boolean(fix.uplinkIfName) : Boolean(fix.localVlanId);
  };
  const allVlansResolved = missingVlans.every(vlanFixComplete);

  const setFix = (vlanId: number, patch: Partial<MigrationVlanResolution>) =>
    setVlanFixes((prev) => {
      const next = new Map(prev);
      const cur = next.get(vlanId) ?? { vlanId, action: 'add-to-uplink' as const };
      next.set(vlanId, { ...cur, ...patch, vlanId });
      return next;
    });

  const toggle = (serial: string) => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });
  };

  return (
    <WizardDialog
      title={`ONU migration · ${olt.name}`}
      subtitle="ONUs that have turned up here unconfigured and are recorded somewhere else — a port, a card, or another OLT. The plan is what you approve; the toggle only lets it run without you."
      maxWidth="5xl"
      bodyClassName="max-h-[70vh] space-y-5 overflow-y-auto p-5"
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            disabled={chosen.size === 0 || movable.length === 0 || !allVlansResolved}
            title={
              !allVlansResolved && missingVlans.length > 0
                  ? `Resolve VLAN ${missingVlans.filter((v) => !vlanFixComplete(v)).join(', ')} first — ${
                      missingVlans.filter((v) => !vlanFixComplete(v)).length === 1 ? 'it is' : 'they are'
                    } not on ${olt.name}.`
                  : undefined
            }
            onClick={() => {
              const added = missingVlans.filter((v) => vlanFixes.get(v)?.action === 'add-to-uplink');
              const replaced = missingVlans.filter((v) => vlanFixes.get(v)?.action === 'replace');
              const vlanNote =
                missingVlans.length > 0
                  ? ` ${added.length > 0 ? `VLAN ${added.join(', ')} added to an uplink here; ` : ''}${
                      replaced.length > 0 ? `VLAN ${replaced.join(', ')} replaced on the ONU. ` : ''
                    }`
                  : '';
              if (
                confirm(
                  `Migrate ${chosen.size} ONU${chosen.size === 1 ? '' : 's'} onto ${olt.name}? Each is authorised there, its record and splitter chain follow it, and its stored VLANs and ports are re-stated.${vlanNote} Nothing is removed from the old slot.`,
                )
              ) {
                startRun();
              }
            }}
          >
            Apply
          </Button>
        </div>
      }
    >
          {error && <ErrorNotice error={error} />}


          {/* --- The armed window ------------------------------------- */}
          <Card title="Auto-authorise" bodyClassName="space-y-4 p-4">
              {armed ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Pill tone="warn" dot>
                    Armed · {countdown(remaining)} left
                  </Pill>
                  <span className="text-sm text-muted">
                    {armed.usedCount} of {armed.maxOnus} ONUs used
                    {armed.presetName ? ` · ${armed.presetName}` : ''}
                    {armed.sourceOltName ? ` · from ${armed.sourceOltName}` : ''}
                    {armed.sourceSlots && armed.sourceSlots.length > 0
                      ? ` · slots ${armed.sourceSlots.join(', ')}`
                      : ''}
                    {armed.ponPort ? ` · ${armed.ponPort} only` : ''}
                  </span>
                  <Button
                    className="ml-auto"
                    variant="danger"
                    loading={disarm.isPending}
                    onClick={() => disarm.mutate()}
                  >
                    Disarm now
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted">
                    Off. While armed, SimpleACS checks this chassis once a minute and migrates
                    anything the plan would have migrated — nothing else, never more than the cap,
                    and only until the window runs out on its own.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field
                      label="Preset"
                      hint="Required to ARM — an unattended authorise has nobody to ask. A manual migrate below needs none: it carries each ONU's own config."
                    >
                      <select
                        className="input"
                        value={presetId}
                        onChange={(e) => setPresetId(e.target.value)}
                      >
                        <option value="">Choose a preset…</option>
                        {(presets.data?.items ?? []).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.vendor ? ` · ${p.vendor.toUpperCase()}` : ''}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      label="Cap"
                      hint={`The most this one window may ever migrate. ${AUTO_AUTHORIZE_MAX_CAP} is one full PON port.`}
                    >
                      <input
                        className="input"
                        type="number"
                        min={1}
                        max={AUTO_AUTHORIZE_MAX_CAP}
                        value={cap}
                        onChange={(e) => setCap(Number(e.target.value))}
                      />
                    </Field>
                    <Field label="Only from" hint="Optional. Ignore ONUs recorded anywhere else.">
                      <select
                        className="input"
                        value={sourceOltId}
                        onChange={(e) => {
                          setSourceOltId(e.target.value);
                          // Slot numbers belong to the chassis they were picked
                          // on; a new source starts from "every slot".
                          setSourceSlots([]);
                        }}
                      >
                        <option value="">Any OLT</option>
                        {/* This chassis is a real source: a port re-patch and a
                            re-cabled card both start and end here. */}
                        <option value={olt.id}>{olt.name} — port or card moves only</option>
                        {otherOlts.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    {sourceOltId && (
                      <Field
                        label="Only from slots"
                        hint={
                          sourceSlots.length === 0
                            ? 'Optional. None chosen = every card of that OLT. Pick the cards being re-cabled.'
                            : `Only ONUs recorded on ${sourceSlots.length === 1 ? 'card' : 'cards'} ${sourceSlots.join(', ')} of the source count.`
                        }
                      >
                        {sourcePortsQuery.isPending ? (
                          <div className="flex items-center gap-2 text-sm text-muted">
                            <Spinner /> Reading its PON ports…
                          </div>
                        ) : sourceSlotOptions.length === 0 ? (
                          <p className="text-sm text-muted">
                            No PON ports recorded for that OLT yet, so every slot is accepted.
                          </p>
                        ) : (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Button
                              size="sm"
                              variant={sourceSlots.length === 0 ? 'primary' : 'secondary'}
                              onClick={() => setSourceSlots([])}
                            >
                              All slots
                            </Button>
                            {sourceSlotOptions.map((s) => {
                              const on = sourceSlots.includes(s.key);
                              return (
                                <Button
                                  key={s.key}
                                  size="sm"
                                  variant={on ? 'primary' : 'secondary'}
                                  title={`${s.ports} PON port${s.ports === 1 ? '' : 's'}`}
                                  onClick={() =>
                                    setSourceSlots((prev) =>
                                      on ? prev.filter((k) => k !== s.key) : [...prev, s.key],
                                    )
                                  }
                                >
                                  {s.key}
                                </Button>
                              );
                            })}
                          </div>
                        )}
                      </Field>
                    )}
                    <Field label="Only on PON port" hint="Optional, e.g. 1/3/9.">
                      <input
                        className="input"
                        value={ponPort}
                        placeholder="shelf/slot/port"
                        onChange={(e) => setPonPort(e.target.value)}
                      />
                    </Field>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={finalize}
                      onChange={(e) => setFinalize(e.target.checked)}
                    />
                    Save the running config to flash after each move
                  </label>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase tracking-wide text-faint">Arm for</span>
                    {AUTO_AUTHORIZE_DURATIONS.map((d) => (
                      <Button
                        key={d}
                        size="sm"
                        variant={duration === d ? 'primary' : 'secondary'}
                        onClick={() => setDuration(d)}
                      >
                        {AUTO_AUTHORIZE_DURATION_LABELS[d]}
                      </Button>
                    ))}
                    <Button
                      className="ml-auto"
                      variant="primary"
                      disabled={!presetId}
                      loading={arm.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            `Let SimpleACS migrate ONUs onto ${olt.name} by itself for ${AUTO_AUTHORIZE_DURATION_LABELS[duration]}, up to ${cap} of them? It stops on its own when the window runs out, and you can disarm it at any time.`,
                          )
                        ) {
                          arm.mutate();
                        }
                      }}
                    >
                      Arm
                    </Button>
                  </div>
                </>
              )}
          </Card>

          {/* --- The plan --------------------------------------------- */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              loading={preview.isPending}
              onClick={() => preview.mutate()}
            >
              Preview
            </Button>
            {plan && (
              <span className="text-sm text-muted">
                {plan.actionable} of {plan.items.length} would move · capped at {plan.cap}
                {plan.presetName ? ` · ${plan.presetName}` : ' · carrying each ONU’s own config'}
              </span>
            )}
          </div>
          <p className="text-xs text-muted">
            No preset needed — a move keeps the configuration each ONU already has. Choose a preset
            in Auto-authorise above only to reconfigure the subscriber during the move.
          </p>

          {plan?.warnings.map((w) => (
            <p
              key={w}
              className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
            >
              {w}
            </p>
          ))}

          {preview.isPending && (
            <div className="flex justify-center p-6">
              <Spinner />
            </div>
          )}

          {plan && plan.items.length === 0 && (
            <p className="text-sm text-muted">
              Nothing is waiting unconfigured on {olt.name}.
            </p>
          )}

          {plan && plan.items.length > 0 && (
            <Table>
              <THead>
                <th className="th" />
                <th className="th">Serial</th>
                <th className="th">Subscriber</th>
                <th className="th">Move</th>
                <th className="th">From → to</th>
                <th className="th">Old slot</th>
                <th className="th">Verdict</th>
              </THead>
              <TBody>
                {plan.items.map((item) => (
                  <MigrationRow
                    key={item.serial}
                    item={item}
                    checked={chosen.has(item.serial)}
                    onToggle={() => toggle(item.serial)}
                  />
                ))}
              </TBody>
            </Table>
          )}

          {/* --- VLANs the destination does not carry ----------------- */}
          {missingVlans.length > 0 && (
            <div className="space-y-3 rounded-lg border border-warn/40 bg-warn-soft/50 p-4">
              <div>
                <h3 className="text-sm font-semibold text-ink">VLANs not on {olt.name}</h3>
                <p className="mt-0.5 text-xs text-muted">
                  The ONUs you picked carry {missingVlans.length === 1 ? 'a VLAN' : 'VLANs'} this
                  chassis does not have. For each, add it to an uplink here — the subscriber keeps
                  their VLAN — or replace it on the ONU with one this OLT already carries. Adding to
                  an uplink is additive: it does not touch any VLAN already on the port.
                </p>
              </div>
              {missingVlans.map((v) => {
                const fix = vlanFixes.get(v);
                const action = fix?.action ?? 'add-to-uplink';
                return (
                  <div
                    key={v}
                    className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface px-3 py-2"
                  >
                    <span className="font-mono text-sm font-semibold text-ink">VLAN {v}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant={action === 'add-to-uplink' ? 'primary' : 'secondary'}
                        onClick={() => setFix(v, { action: 'add-to-uplink' })}
                      >
                        Add to uplink
                      </Button>
                      <Button
                        size="sm"
                        variant={action === 'replace' ? 'primary' : 'secondary'}
                        onClick={() => setFix(v, { action: 'replace' })}
                      >
                        Replace on ONU
                      </Button>
                    </div>
                    {action === 'add-to-uplink' ? (
                      <select
                        className="input max-w-[16rem]"
                        value={fix?.uplinkIfName ?? ''}
                        onChange={(e) => setFix(v, { uplinkIfName: e.target.value || undefined })}
                      >
                        <option value="">Choose an uplink…</option>
                        {(uplinksQuery.data?.items ?? []).map((u) => (
                          <option key={u.name} value={u.name}>
                            {u.name}
                            {u.operStatus === 'up' ? '' : ` · ${u.operStatus}`}
                            {u.taggedVlans.includes(v) ? ' · already tagged' : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        className="input max-w-[16rem]"
                        value={fix?.localVlanId ?? ''}
                        onChange={(e) =>
                          setFix(v, {
                            localVlanId: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                      >
                        <option value="">Choose a local VLAN…</option>
                        {(plan?.destinationVlanIds ?? []).map((lv) => (
                          <option key={lv} value={lv}>
                            VLAN {lv}
                          </option>
                        ))}
                      </select>
                    )}
                    {!vlanFixComplete(v) && (
                      <span className="text-xs font-medium text-warn">needs a choice</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
    </WizardDialog>
  );
}

function MigrationRow({
  item,
  checked,
  onToggle,
}: {
  item: MigrationPlanItem;
  checked: boolean;
  onToggle: () => void;
}) {
  const movable = item.verdict === 'migration';
  return (
    <>
      <TRow>
        <td className="td">
          <input type="checkbox" checked={checked} disabled={!movable} onChange={onToggle} />
        </td>
        <td className="td font-mono text-xs">{item.serial}</td>
        <td className="td">
          {item.name ?? <span className="text-faint">—</span>}
          {item.onuNumber !== null && <span className="ml-1 text-faint">#{item.onuNumber}</span>}
        </td>
        <td className="td text-muted">{MOVE_CLASS_LABELS[item.moveClass]}</td>
        <td className="td font-mono text-xs">
          {item.fromOltName ? `${item.fromOltName} ${item.fromPonPort}:${item.fromOnuIndex}` : '—'}
          {' → '}
          {item.toPonPort}
          {item.toOnuId !== null ? `:${item.toOnuId}` : ''}
        </td>
        <td className="td text-muted">{ONU_PRESENCE_LABELS[item.presence]}</td>
        <td className="td">
          <Pill state tone={VERDICT_TONE[item.verdict]}>{MIGRATION_VERDICT_LABELS[item.verdict]}</Pill>
        </td>
      </TRow>
      <TRow>
        <td className="td" />
        <td className="td text-xs text-muted" colSpan={6}>
          {item.reason}
          {item.config && (
            <span className="ml-1 text-faint">
              · VLAN {item.config.dataVlan ?? '—'}
              {item.config.managementVlan ? ` / mgmt ${item.config.managementVlan}` : ''} ·{' '}
              {item.config.vlanMode} · {item.config.addressing} ·{' '}
              {item.config.onuType ?? 'no ONU type'}
            </span>
          )}
        </td>
      </TRow>
    </>
  );
}
